// ---------------------------------------------------------------------------
// Persona Service (Unified)
// ---------------------------------------------------------------------------
// Unified API client for managing personas across Funnel, Discovery & Compare.
// Replaces separate persona methods in funnelService + userFiltersService.
// All personas now live in discovery_personas table via /api/v11/discovery/personas.
// ---------------------------------------------------------------------------

import { api } from '@/lib/api'
import type { DimensionType, UnitType } from '@/components/v11/funnel/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PersonaLayer {
  readonly type: DimensionType
  readonly dimension: string
  readonly values: readonly (string | number)[]
  readonly dimensionLabel: string
  readonly valueLabels: readonly string[]
}

export type PersonaType = 'funnel' | 'flat'

export interface Persona {
  readonly id: number
  readonly name: string
  readonly description: string | null
  readonly personaType: PersonaType
  readonly datasource: string
  readonly layers: readonly PersonaLayer[]
  readonly layerCount: number
  readonly flatFilters: Readonly<Record<string, string>>
  readonly finalCount: number
  readonly finalUnit: string
  readonly totalOrigin: number
  readonly isDefault: boolean
  readonly display: number
  readonly createdAt: string
  readonly updatedAt: string
}

export interface CreatePersonaInput {
  readonly name: string
  readonly description?: string
  readonly persona_type: PersonaType
  readonly layers: readonly {
    readonly type: DimensionType
    readonly dimension: string
    readonly values: readonly (string | number)[]
    readonly dimensionLabel?: string
    readonly valueLabels?: readonly string[]
  }[]
  readonly datasource?: string
  readonly finalCount?: number
  readonly finalUnit?: UnitType
  readonly totalOrigin?: number
  readonly is_default?: boolean
  readonly display?: number
}

export interface UpdatePersonaInput {
  readonly name?: string
  readonly description?: string
  readonly layers?: readonly {
    readonly type: DimensionType
    readonly dimension: string
    readonly values: readonly (string | number)[]
    readonly dimensionLabel?: string
    readonly valueLabels?: readonly string[]
  }[]
  readonly datasource?: string
  readonly finalCount?: number
  readonly finalUnit?: UnitType
  readonly totalOrigin?: number
  readonly is_default?: boolean
  readonly display?: number
}

// ---------------------------------------------------------------------------
// Response Types
// ---------------------------------------------------------------------------

interface PersonaResponse {
  readonly success: boolean
  readonly data: Persona
}

interface PersonaListResponse {
  readonly success: boolean
  readonly data: readonly Persona[]
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const BASE_URL = '/api/v11/discovery/personas'

export const personaService = {
  /**
   * List all personas for the current user.
   * Optional type filter: 'funnel' or 'flat'.
   */
  async getAll(type?: PersonaType): Promise<readonly Persona[]> {
    const params = type ? { type } : undefined
    const response = await api.get<PersonaListResponse>(BASE_URL, { params })
    return (response as unknown as PersonaListResponse).data
  },

  /**
   * Get a specific persona by ID.
   */
  async getById(id: number): Promise<Persona> {
    const response = await api.get<PersonaResponse>(`${BASE_URL}/${id}`)
    return (response as unknown as PersonaResponse).data
  },

  /**
   * Get the user's default persona.
   */
  async getDefault(): Promise<Persona | null> {
    try {
      const response = await api.get<PersonaResponse>(`${BASE_URL}/default`)
      return (response as unknown as PersonaResponse).data
    } catch (error: unknown) {
      const err = error as { response?: { status?: number } }
      if (err?.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Create a new persona.
   */
  async create(input: CreatePersonaInput): Promise<Persona> {
    const response = await api.post<PersonaResponse>(BASE_URL, input)
    return (response as unknown as PersonaResponse).data
  },

  /**
   * Update an existing persona.
   */
  async update(id: number, input: UpdatePersonaInput): Promise<Persona> {
    const response = await api.put<PersonaResponse>(`${BASE_URL}/${id}`, input)
    return (response as unknown as PersonaResponse).data
  },

  /**
   * Delete a persona by ID.
   */
  async delete(id: number): Promise<void> {
    await api.delete(`${BASE_URL}/${id}`)
  },

  /**
   * Set a persona as the user's default.
   */
  async setDefault(id: number): Promise<Persona> {
    const response = await api.post<PersonaResponse>(
      `${BASE_URL}/${id}/set-default`
    )
    return (response as unknown as PersonaResponse).data
  },
}
